import { useQuery } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { ProjectService } from 'src/services/project.service'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { useTranslation } from 'react-i18next'
import {
  CurecallButton,
  CurecallLogoSecondaryColor,
  CurecallTooltip,
  ECurecallButtonAppearance,
  PrintIcon,
} from '@honestica/curecall-ui'
// @ts-expect-error - tailwind.config has no type declarations
import tailwindConfig from '../../../../tailwind.config'
import { renderToString } from 'react-dom/server'
import { getFormattedDateWithHours } from 'src/utils/date-formatter.util'
import { useEffect, useState } from 'react'
import { isOnElectronApp, isOnRecentCurecallElectronAppVersion } from 'src/utils/electron.util'
import PageTitle from 'src/components/page-title/page-title.component'
import { FormStatistics } from './_components/form-statistics.component'
import { GeneralStatistics } from './_components/general-statistics.component'

export default function StatisticsPage() {
  const { t } = useTranslation()
  const { project } = useCurrentProject()

  const {
    isLoading: isLoadingStatistics,
    error: statisticsError,
    data: projectStatistics,
  } = useQuery({
    queryKey: ['project-statistics', project.id],
    queryFn: () => ProjectService.getStatistics(project.id),
  })
  const [canShowButton, setCanShowButton] = useState(false)

  const isElectron = isOnElectronApp()

  const downloadStatistics = () => {
    const contentToPrintHtml = document.getElementById('statistics-printable-page')?.innerHTML
    if (!contentToPrintHtml) {
      return
    }
    const logoHTML = renderToString(<CurecallLogoSecondaryColor className="c-w-[200px]" />)
    const printDateAndHour = getFormattedDateWithHours(t, new Date())

    if (isOnRecentCurecallElectronAppVersion()) {
      // Template without embedded footer for Electron app, the custom footer is added later at the printToPDF stage (footerTemplate)
      const htmlToPrint = `
        <html>
          <head>
            <title>${t('pages.statistics.file-name')}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.tailwindcss.com"></script>
            <script>tailwind.config = {
              theme: ${JSON.stringify(tailwindConfig.theme)},
              prefix: ${JSON.stringify(tailwindConfig.prefix)}
            }</script>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Nunito">
            <style>
              * { font-family: Nunito, sans-serif !important; }
              .hide-for-print { display: none; }
              .statistics-form-stat-block {
                page-break-inside: "avoid";
              }
            </style>
          </head>
          <body class="c-m-6">
            <div class="c-flex c-justify-between c-items-end c-pb-2 c-border-b c-border-grey-200">
              ${logoHTML}
              <span>${printDateAndHour}</span>
            </div>
            ${contentToPrintHtml}
          </body>
        </html>`
      window.electronAPI.print(htmlToPrint, {
        displayHeaderFooter: true,
        headerTemplate: '<div></div>',
        footerTemplate: `
          <div style="display: flex; justify-content: center; align-items: center; font-size: 10px; color: lightgray; width: 100%; padding-top: 5px;">
            <span>${t(
              'pages.statistics.statistics-generated-by-curecall',
            )}</span> <span style="margin-left: 5px; margin-right: 5px;"> - </span> <span class="pageNumber"></span>/<span class="totalPages"></span>
          </div>`,
      })
    } else {
      // Template containing an embedded footer for classic browser print-to-pdf
      const htmlToPrint = `
        <html>
          <head>
            <title>${t('pages.statistics.file-name')}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.tailwindcss.com"></script>
            <script>tailwind.config = {
              theme: ${JSON.stringify(tailwindConfig.theme)},
              prefix: ${JSON.stringify(tailwindConfig.prefix)}
            }</script>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Nunito">
            <style>
              * { font-family: Nunito, sans-serif !important; }
              .hide-for-print { display: none; }
              .statistics-form-stat-block {
                page-break-inside: "avoid";
              }
            </style>
          </head>
          <body class="c-m-6">
            <table class="c-w-full">
              <tbody>
                <tr>
                  <td>
                    <div class="c-flex c-justify-between c-items-end c-pb-2 c-border-b c-border-grey-200">
                      ${logoHTML}
                      <span>${printDateAndHour}</span>
                    </div>
                    ${contentToPrintHtml}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="c-flex c-justify-center">${renderToString(
                    <CurecallLogoSecondaryColor className="c-w-[130px] c-mr-4" />,
                  )} ${t('pages.statistics.statistics-generated-by-curecall')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </body>
        </html>`
      const windowOpened = window.open()
      if (contentToPrintHtml && windowOpened) {
        windowOpened.document.write(htmlToPrint)
        windowOpened.document.write(
          '<script type="text/javascript"> window.onload = function() { window.print(); window.close(); };</script>',
        )
        windowOpened.document.close()
        windowOpened.focus()
      }
    }
  }

  useEffect(() => {
    if (isElectron) {
      setCanShowButton(isOnRecentCurecallElectronAppVersion())
    } else {
      setCanShowButton(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (statisticsError) return <ErrorQueryHandler error={statisticsError} />
  if (isLoadingStatistics) {
    return <div className="loading" />
  }

  const isStatisticPageUnderMaintenance = import.meta.env?.VITE_IS_STATISTIC_PAGE_ACTIVATED !== 'true'

  return (
    <div id="statistics-printable-page">
      <div className="c-pb-2 c-flex c-justify-between">
        <PageTitle>{t('pages.statistics.title', { projectName: project.name })}</PageTitle>
        {!isStatisticPageUnderMaintenance &&
          (canShowButton ? (
            <CurecallButton
              appearance={ECurecallButtonAppearance.SECONDARY}
              onClick={downloadStatistics}
              className="hide-for-print"
            >
              <div className="c-flex c-items-center">
                <PrintIcon className="c-w-4 c-mr-2" />
                {t('pages.statistics.print')}
              </div>
            </CurecallButton>
          ) : (
            <CurecallTooltip
              content={t('pages.statistics.app-is-not-up-to-date')}
              tooltipPosition="bottom-end"
            >
              <CurecallButton
                appearance={ECurecallButtonAppearance.SECONDARY}
                isDisabled={true}
                className="hide-for-print"
              >
                <div className="c-flex c-items-center">
                  <PrintIcon className="c-w-4 c-mr-2" />
                  {t('pages.statistics.print')}
                </div>
              </CurecallButton>
            </CurecallTooltip>
          ))}
      </div>

      <GeneralStatistics projectStatistics={projectStatistics} />
      <FormStatistics projectStatistics={projectStatistics} />
    </div>
  )
}
